import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  ShoppingCartIcon,
  ChartBarIcon,
  UserGroupIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  CogIcon,
  BoltIcon,
  StarIcon,
  CurrencyDollarIcon,
  TruckIcon,
  InboxIcon,
  DocumentTextIcon,
  BuildingStorefrontIcon,
  SparklesIcon,
} from '@heroicons/react/24/solid';

const ICON_MAP: Record<string, any> = {
  UserGroupIcon,
  ShoppingCartIcon,
  ChartBarIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  CogIcon,
  BoltIcon,
  StarIcon,
  CurrencyDollarIcon,
  TruckIcon,
  InboxIcon,
  DocumentTextIcon,
  BuildingStorefrontIcon,
  SparklesIcon,
};

const DEFAULT_FEATURES = [
  { title: 'Customer Management', desc: 'Manage wholesale clients with custom pricing, credit limits, and discount tiers. Every customer gets their own branded portal.', icon: 'UserGroupIcon', color: '#3b82f6' },
  { title: 'Order System', desc: 'Customers browse your catalog, add to cart, and place orders. Real-time order tracking from pending to delivered.', icon: 'ShoppingCartIcon', color: '#10b981' },
  { title: 'Financial Dashboard', desc: 'Track revenue, outstanding balances, and payment history with beautiful charts and exportable reports.', icon: 'ChartBarIcon', color: '#8b5cf6' },
  { title: 'White-Label Portals', desc: 'Each company gets their own subdomain with custom branding, logo, and login page. Your brand, their experience.', icon: 'GlobeAltIcon', color: '#f59e0b' },
  { title: 'Role-Based Access', desc: 'Three-tier hierarchy: Admin, Company Manager, and Customer. Everyone sees exactly what they need.', icon: 'ShieldCheckIcon', color: '#ef4444' },
  { title: 'Mobile-First Design', desc: 'Fully responsive with native-like mobile navigation. Your customers can order from anywhere.', icon: 'DevicePhoneMobileIcon', color: '#06b6d4' },
];

const DEFAULT_STEPS = [
  { num: '01', title: 'Create Your Account', desc: 'Set up your bakery profile and configure your product catalog with categories and pricing.' },
  { num: '02', title: 'Add Your Clients', desc: 'Invite wholesale customers, set their discounts, credit limits, and branded portal.' },
  { num: '03', title: 'Start Receiving Orders', desc: 'Customers log in to their portal, browse products, and place orders. You manage everything from one dashboard.' },
];

export interface HomepageData {
  hero_badge: string;
  hero_title_line1: string;
  hero_title_highlight: string;
  hero_title_line3: string;
  hero_description: string;
  hero_cta_primary: string;
  hero_cta_secondary: string;
  hero_cta_tertiary: string;
  hero_trust_badges: string[];
  features_badge: string;
  features_title: string;
  features_description: string;
  features: { title: string; desc: string; icon: string; color: string }[];
  steps_badge: string;
  steps_title: string;
  steps: { num: string; title: string; desc: string }[];
  portals_title: string;
  portals_description: string;
  portals_features: string[];
  portals_mockup_name: string;
  portals_mockup_subtitle: string;
  cta_title_line1: string;
  cta_title_highlight: string;
  cta_description: string;
  cta_button_text: string;
}

const DEFAULTS: HomepageData = {
  hero_badge: 'Built for wholesale bakeries',
  hero_title_line1: 'Manage your wholesale',
  hero_title_highlight: 'bakery business',
  hero_title_line3: 'in one place',
  hero_description: 'Customer portals, order management, financial tracking, and branded subdomains — everything your wholesale bakery needs to grow.',
  hero_cta_primary: 'Start Free',
  hero_cta_secondary: 'View Pricing',
  hero_cta_tertiary: 'Learn More',
  hero_trust_badges: ['Free setup', 'Custom branding', 'No credit card'],
  features_badge: 'Features',
  features_title: 'Everything you need to run your wholesale operation',
  features_description: 'From order management to financial reporting, Sourdoughsy gives you the tools to scale your bakery business.',
  features: DEFAULT_FEATURES,
  steps_badge: 'How It Works',
  steps_title: 'Up and running in minutes',
  steps: DEFAULT_STEPS,
  portals_title: 'Your brand, their portal',
  portals_description: 'Every company gets a custom subdomain like yourcompany.sourdoughsy.com with their own logo on the login page. Your wholesale customers feel like they have their own dedicated platform.',
  portals_features: ['Custom company subdomain', 'Upload your own logo', 'Branded login experience', 'Separate customer credentials'],
  portals_mockup_name: 'Acme Bakery Co.',
  portals_mockup_subtitle: 'Customer Portal',
  cta_title_line1: 'Ready to streamline your',
  cta_title_highlight: 'wholesale bakery?',
  cta_description: 'Join bakeries that manage their entire wholesale operation with Sourdoughsy.',
  cta_button_text: "Get Started — It's Free",
};

export const useHomepage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState<HomepageData>(DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://127.0.0.1:8001/api' : '/api');
        const res = await fetch(`${baseUrl}/site/homepage/`);
        if (res.ok) {
          const data = await res.json();
          setContent({ ...DEFAULTS, ...data });
        }
      } catch {
        // Use defaults on error
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  // Resolve icon strings to actual components
  const features = (content.features?.length ? content.features : DEFAULT_FEATURES).map(f => ({
    ...f,
    icon: ICON_MAP[f.icon] || UserGroupIcon,
  }));

  const steps = content.steps?.length ? content.steps : DEFAULT_STEPS;

  return { navigate, content, features, steps, loading };
};
